import { useCallback, useEffect, useRef, useState } from "react";
import RealtimeKitClient from "@cloudflare/realtimekit";
import { RealtimeKitRecording } from "@cloudflare/realtimekit-recording-sdk";

/**
 * Self-healing room link for the Program Output page (the broadcast).
 *
 * One RealtimeKit client renders the program. When its socket / receive transport is down for
 * TROUBLE_MS, the room throws us out, or the on-air tiles stop painting, a fresh client with the
 * same token is joined and swapped in; the dead one is left afterwards so the picture never drops
 * to the holding card on purpose. The recording SDK is initialised once with autoStop OFF: the
 * job only ends when a producer stops it (or ends the show, which ends the room).
 */
const TROUBLE_MS = 15000;
const NO_FRAMES_MS = 30000;
const STALL_COOLDOWN_MS = 120000;
const backoff = (n) => Math.min(30000, 2000 * 2 ** n);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Keep the container-facing getters pointed at the live client.
function patchRecorderGetters(m) {
  ["realtimeKitRecording", "dyteRecording"].forEach((k) => {
    const o = typeof window !== "undefined" ? window[k] : null;
    if (!o) return;
    o.getParticipantCount = () => m.participants.active.size;
    o.getMeeting = () => m;
    o.isMeetingJoined = () => true;
  });
}

export function useRecorderLink(authToken, { recorder = false, onEvent } = {}) {
  const [meeting, setMeeting] = useState(null);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine !== false);
  const [sock, setSock] = useState("connected");
  const [recv, setRecv] = useState("connected");
  const cur = useRef(null);
  const sdk = useRef(null);
  const alive = useRef(true);
  const busy = useRef(false);
  const started = useRef(false);
  const attemptRef = useRef(0);
  const flow = useRef({ tiles: 0, flowing: 0 });
  const lastFramesAt = useRef(Date.now());
  const everFlowed = useRef(false);
  const lastStallRebuild = useRef(0);
  const evtRef = useRef(onEvent);
  evtRef.current = onEvent;
  const evt = useCallback((event, detail) => { try { evtRef.current?.(event, detail); } catch (e) { /* noop */ } }, []);

  const rebuild = useCallback(async (reason) => {
    if (busy.current || !alive.current || !authToken) return;
    busy.current = true;
    const n = attemptRef.current;
    attemptRef.current = n + 1;
    setAttempt(n + 1);
    evt("rebuilding link", `${reason} · try ${n + 1}`);
    await sleep(backoff(n));
    if (!alive.current) { busy.current = false; return; }
    const old = cur.current;
    // The dead client must not be able to end the job (the SDK stops on a 'kicked' roomLeft).
    if (old) { try { old.self.removeAllListeners(); } catch (e) { /* noop */ } }
    try {
      const m = await RealtimeKitClient.init({ authToken, defaults: { audio: false, video: false } });
      if (!alive.current) { try { m.leave(); } catch (e) { /* noop */ } busy.current = false; return; }
      await m.join();
      if (!alive.current) { try { m.leave(); } catch (e) { /* noop */ } busy.current = false; return; }
      cur.current = m;
      patchRecorderGetters(m);
      setMeeting(m);
      if (old) { try { old.leave(); } catch (e) { /* noop */ } }
      attemptRef.current = 0;
      setAttempt(0);
      lastFramesAt.current = Date.now();
      evt("link rebuilt", `after ${reason}`);
      busy.current = false;
    } catch (e) {
      evt("rebuild failed", e?.message || String(e));
      busy.current = false;
      rebuild(reason);
    }
  }, [authToken, evt]);

  // First connection. In recorder mode the SDK joins for us and tells the container to capture.
  useEffect(() => {
    alive.current = true;
    if (!authToken || started.current) return undefined;
    started.current = true;
    (async () => {
      try {
        const m = await RealtimeKitClient.init({ authToken, defaults: { audio: false, video: false } });
        if (!alive.current) return;
        cur.current = m;
        setMeeting(m);
        if (recorder) {
          const s = new RealtimeKitRecording({ autoStart: true, autoStop: false });
          sdk.current = s;
          await s.init(m);
        } else {
          await m.join();
        }
        evt("recorder joined");
      } catch (e) {
        setError(e?.message || "Could not join the studio room");
        evt("join failed", e?.message || String(e));
      }
    })();
    return undefined;
  }, [authToken, recorder, evt]);

  useEffect(() => () => {
    alive.current = false;
    const m = cur.current;
    cur.current = null;
    if (m) { try { m.leave(); } catch (e) { /* noop */ } }
    try { sdk.current?.cleanup(); } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  // Watch the current client.
  useEffect(() => {
    if (!meeting) return undefined;
    setJoined(!!meeting.self.roomJoined);
    setSock("connected");
    setRecv("connected");
    const onJoined = () => setJoined(true);
    const onLeft = ({ state } = {}) => {
      if (state === "left") return;
      setJoined(false);
      if (state === "ended") {
        evt("room ended");
        try { sdk.current?.stopRecording(); } catch (e) { /* noop */ }
        return;
      }
      rebuild(`room ${state || "lost"}`);
    };
    const onMedia = ({ transport, state } = {}) => { if (!transport || transport === "recv") setRecv(state || "connected"); };
    const onSocket = ({ state } = {}) => setSock(state || "connected");
    meeting.self.on("roomJoined", onJoined);
    meeting.self.on("roomLeft", onLeft);
    meeting.meta?.on?.("mediaConnectionUpdate", onMedia);
    meeting.meta?.on?.("socketConnectionUpdate", onSocket);
    return () => {
      try {
        meeting.self.off("roomJoined", onJoined);
        meeting.self.off("roomLeft", onLeft);
        meeting.meta?.off?.("mediaConnectionUpdate", onMedia);
        meeting.meta?.off?.("socketConnectionUpdate", onSocket);
      } catch (e) { /* noop */ }
    };
  }, [meeting, rebuild, evt]);

  const linkDown = !online || sock !== "connected" || ["failed", "disconnected"].includes(recv);
  const linkWhy = !online ? "offline" : sock !== "connected" ? `socket ${sock}` : `media ${recv}`;
  useEffect(() => {
    if (!linkDown) return undefined;
    const id = setTimeout(() => rebuild(linkWhy), TROUBLE_MS);
    return () => clearTimeout(id);
  }, [linkDown, linkWhy, rebuild]);

  // Frame watchdog: tiles on air, frames used to flow, nothing painted for NO_FRAMES_MS.
  const onFlow = useCallback((f) => {
    flow.current = f;
    if (f.flowing > 0) { lastFramesAt.current = Date.now(); everFlowed.current = true; }
  }, []);
  useEffect(() => {
    if (!joined) return undefined;
    lastFramesAt.current = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      if (flow.current.tiles > 0 && everFlowed.current && now - lastFramesAt.current > NO_FRAMES_MS && now - lastStallRebuild.current > STALL_COOLDOWN_MS) {
        lastStallRebuild.current = now;
        rebuild("no frames");
      }
    }, 1000);
    return () => clearInterval(id);
  }, [joined, rebuild]);

  return { meeting, joined, error, attempt, linkDown, linkWhy, onFlow };
}

export default useRecorderLink;
